// Buildings blowing apart in the 3D look. When a building is destroyed it bursts into
// chunks made of its own materials, which fly out, tumble, bounce and slide along the
// ground, and topple onto their flattest side as they settle; a flash and a column of
// smoke go up with them. Settled chunks are not dead weight: a blast near them wakes them
// and throws them again (see kick). Purely visual: the chunks run on their own little
// physics step in the render loop and never touch the deterministic sim.
import * as THREE from 'three';

const GRAVITY = 20;
/** Of the speed into the ground, how much a chunk keeps as bounce, and how much of its slide and spin survive a landing. */
const BOUNCE = 0.32;
const SLIDE = 0.55;
const SPIN_KEEP = 0.6;
/** A chunk slower than this on the ground stops for good. */
const REST = 0.35;
/** Settled chunks kept on the map at once; the oldest go first. */
const MAX_CHUNKS = 900;
const FLASH_MS = 320;
const SMOKE_MS = 2600;

/** Rough rocks and slabs, each a slightly different lump, shared by every chunk. */
const SHAPES: THREE.BufferGeometry[] = (() => {
  let seed = 11;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  const lump = (g: THREE.BufferGeometry, jitter: number) => {
    const p = g.getAttribute('position') as THREE.BufferAttribute;
    // move matching corners together so the lump stays closed
    const moved = new Map<string, [number, number, number]>();
    for (let i = 0; i < p.count; i++) {
      const key = `${p.getX(i).toFixed(3)},${p.getY(i).toFixed(3)},${p.getZ(i).toFixed(3)}`;
      let d = moved.get(key);
      if (!d) { d = [(rnd() - 0.5) * jitter, (rnd() - 0.5) * jitter, (rnd() - 0.5) * jitter]; moved.set(key, d); }
      p.setXYZ(i, p.getX(i) + d[0], p.getY(i) + d[1], p.getZ(i) + d[2]);
    }
    g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  };
  const out: THREE.BufferGeometry[] = [];
  for (let k = 0; k < 4; k++) out.push(lump(new THREE.IcosahedronGeometry(0.5, 0), 0.35));
  for (let k = 0; k < 3; k++) out.push(lump(new THREE.BoxGeometry(1, 0.35, 0.7), 0.25)); // slabs of wall and roof
  out.push(lump(new THREE.BoxGeometry(1.4, 0.18, 0.18), 0.08)); // a beam
  for (const g of out) g.userData.shared = true;
  return out;
})();

const FALLBACK = new THREE.MeshStandardMaterial({ color: 0x8a8a84, roughness: 0.9 });
const FLASH_COLOR = new THREE.Color(0xffb060).multiplyScalar(3);
const SMOKE_COLOR = 0x3a3836;

interface Chunk {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  spin: THREE.Vector3;
  /** Half its height, so it rests on the ground rather than in it. */
  r: number;
  asleep: boolean;
  /** Down and slowing: toppling onto its flattest side. */
  settling?: boolean;
  /** Lying flat: it may go to sleep. */
  flat?: boolean;
  key: string;
}

const UP = new THREE.Vector3(0, 1, 0);
const AXES = [new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, 1)];

interface Burst {
  key: string;
  at: number;
  flash: THREE.Mesh;
  smoke: { mesh: THREE.Mesh; drift: THREE.Vector3; delay: number; size: number }[];
}

export class Debris3D {
  readonly group = new THREE.Group();
  private chunks: Chunk[] = [];
  private bursts: Burst[] = [];
  private last = 0;
  private readonly flashGeo = new THREE.SphereGeometry(1, 16, 12);
  private readonly smokeGeo = new THREE.IcosahedronGeometry(1, 1);

  /**
   * Blows a building apart. `materials` are the building's own (so the chunks look like it),
   * `centre` is the middle of its footprint on the ground, `size` its footprint in tiles and
   * `top` its height. `key` names the building, so its chunks can be cleared later.
   */
  explode(key: string, materials: THREE.Material[], centre: THREE.Vector3, size: number, top: number, seed: number, now: number): void {
    let s = (Math.abs(seed) % 2147483646) + 1;
    const rnd = () => ((s = (s * 16807) % 2147483647) / 2147483647);
    const mats = materials.length ? materials : [FALLBACK];
    const count = Math.round(8 + size * size * 3.5);
    const scale = 0.12 + size * 0.05;
    for (let i = 0; i < count; i++) {
      const mesh = new THREE.Mesh(SHAPES[Math.floor(rnd() * SHAPES.length)]!, mats[Math.floor(rnd() * mats.length)]!);
      const k = scale * (0.5 + rnd() * 1.1);
      mesh.scale.set(k * (0.7 + rnd() * 0.6), k * (0.7 + rnd() * 0.6), k * (0.7 + rnd() * 0.6));
      mesh.rotation.set(rnd() * 6.28, rnd() * 6.28, rnd() * 6.28);
      mesh.castShadow = mesh.receiveShadow = true;
      // from somewhere in the building, flung out from its middle and up
      const ox = (rnd() - 0.5) * size * 0.8, oz = (rnd() - 0.5) * size * 0.8;
      mesh.position.set(centre.x + ox, 0.1 + rnd() * top, centre.z + oz);
      const out = new THREE.Vector3(ox, 0, oz);
      if (out.lengthSq() < 1e-4) out.set(rnd() - 0.5, 0, rnd() - 0.5);
      out.normalize().multiplyScalar((1.5 + rnd() * 4.5) * (0.8 + size * 0.15));
      const vel = new THREE.Vector3(out.x, 4 + rnd() * 6, out.z);
      const spin = new THREE.Vector3(rnd() - 0.5, rnd() - 0.5, rnd() - 0.5).multiplyScalar(22);
      this.group.add(mesh);
      this.chunks.push({ mesh, vel, spin, r: k * 0.3, asleep: false, key });
    }
    // drop the oldest settled chunks once there are too many
    while (this.chunks.length > MAX_CHUNKS) {
      const old = this.chunks.shift()!;
      this.group.remove(old.mesh);
    }

    const flash = new THREE.Mesh(this.flashGeo, new THREE.MeshBasicMaterial({ color: FLASH_COLOR, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
    flash.position.set(centre.x, top * 0.5, centre.z);
    flash.userData.size = 0.35 + size * 0.3;
    this.group.add(flash);
    const smoke: Burst['smoke'] = [];
    for (let i = 0; i < 6 + size * 2; i++) {
      const mesh = new THREE.Mesh(this.smokeGeo, new THREE.MeshLambertMaterial({ color: SMOKE_COLOR, transparent: true, depthWrite: false }));
      mesh.position.set(centre.x + (rnd() - 0.5) * size * 0.7, 0.2 + rnd() * top, centre.z + (rnd() - 0.5) * size * 0.7);
      mesh.visible = false;
      this.group.add(mesh);
      smoke.push({ mesh, drift: new THREE.Vector3((rnd() - 0.5) * 0.6, 0.8 + rnd() * 1.2, (rnd() - 0.5) * 0.6), delay: rnd() * 250, size: (0.35 + rnd() * 0.4) * (0.7 + size * 0.25) });
    }
    this.bursts.push({ key, at: now, flash, smoke });
  }

  /**
   * A blast at `centre`: every chunk within `radius` (tiles) is thrown outward and up, harder the
   * nearer it lies, and set tumbling, settled or not.
   */
  kick(centre: THREE.Vector3, radius: number, power: number): void {
    for (const c of this.chunks) {
      const d = Math.hypot(c.mesh.position.x - centre.x, c.mesh.position.z - centre.z);
      if (d > radius) continue;
      const f = power * (1 - d / radius) * (0.7 + Math.random() * 0.6);
      const out = d > 1e-3 ? new THREE.Vector3((c.mesh.position.x - centre.x) / d, 0, (c.mesh.position.z - centre.z) / d) : new THREE.Vector3(Math.random() - 0.5, 0, Math.random() - 0.5).normalize();
      c.vel.addScaledVector(out, f).add(new THREE.Vector3(0, f * (0.8 + Math.random() * 0.6), 0));
      c.spin.add(new THREE.Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).multiplyScalar(f * 5));
      c.asleep = false;
    }
  }

  /** Takes away a building's chunks and smoke (it stands again, or has left the map). */
  clear(key: string): void {
    this.chunks = this.chunks.filter(c => {
      if (c.key !== key) return true;
      this.group.remove(c.mesh);
      return false;
    });
    this.bursts = this.bursts.filter(b => {
      if (b.key !== key) return true;
      this.removeBurst(b);
      return false;
    });
  }

  private removeBurst(b: Burst): void {
    this.group.remove(b.flash);
    (b.flash.material as THREE.Material).dispose();
    for (const p of b.smoke) {
      this.group.remove(p.mesh);
      (p.mesh.material as THREE.Material).dispose();
    }
  }

  /**
   * Turns a chunk on the ground toward lying on its flattest side: its thinnest axis brought round to
   * point straight up (or down), a little each frame, its height following so it stays on the ground.
   */
  private topple(c: Chunk, dt: number): void {
    const s = c.mesh.scale;
    const thin = s.x <= s.y && s.x <= s.z ? 0 : s.y <= s.z ? 1 : 2;
    const axis = AXES[thin]!.clone().applyQuaternion(c.mesh.quaternion);
    const target = axis.y >= 0 ? UP : UP.clone().negate();
    const turn = new THREE.Quaternion().setFromUnitVectors(axis, target);
    const angle = 2 * Math.acos(Math.min(1, Math.abs(turn.w)));
    c.flat = angle < 0.05;
    turn.slerp(new THREE.Quaternion(), Math.max(0, 1 - Math.min(1, dt * 6)));
    c.mesh.quaternion.premultiply(turn);
    // it ends up resting on its thin side: half its thinnest dimension above the ground
    const half = [s.x, s.y, s.z][thin]! * 0.5;
    c.r += (half - c.r) * Math.min(1, dt * 6);
  }

  /** Moves everything on by the time since the last frame. */
  update(now: number): void {
    const dt = Math.min(0.05, Math.max(0, (now - (this.last || now)) / 1000));
    this.last = now;
    const q = new THREE.Quaternion(), axis = new THREE.Vector3();
    for (const c of this.chunks) {
      if (c.asleep) continue;
      c.vel.y -= GRAVITY * dt;
      c.vel.multiplyScalar(1 - 0.15 * dt);
      c.mesh.position.addScaledVector(c.vel, dt);
      const w = c.spin.length();
      if (w > 1e-3) {
        q.setFromAxisAngle(axis.copy(c.spin).divideScalar(w), w * dt);
        c.mesh.quaternion.premultiply(q);
      }
      if (c.mesh.position.y < c.r) {
        c.mesh.position.y = c.r;
        if (c.vel.y < 0) c.vel.y = -c.vel.y * BOUNCE;
        c.vel.x *= SLIDE;
        c.vel.z *= SLIDE;
        c.spin.multiplyScalar(SPIN_KEEP);
        if (c.vel.length() < REST) c.settling = true;
      }
      // on the ground and slowing: it topples onto its flattest side, the way a slab falls flat, then lies still
      if (c.settling) {
        this.topple(c, dt);
        c.vel.multiplyScalar(Math.pow(0.05, dt));
        c.spin.multiplyScalar(Math.pow(0.02, dt));
        if (c.vel.length() < REST * 0.3 && c.spin.length() < 0.2 && c.flat) { c.asleep = true; c.settling = false; }
      }
    }
    for (let i = this.bursts.length - 1; i >= 0; i--) {
      const b = this.bursts[i]!;
      const age = now - b.at;
      const f = age / FLASH_MS;
      b.flash.visible = f < 1;
      if (f < 1) {
        b.flash.scale.setScalar((b.flash.userData.size as number) * (0.5 + f * 1.2));
        (b.flash.material as THREE.MeshBasicMaterial).opacity = (1 - f) * (1 - f);
      }
      let alive = f < 1;
      for (const p of b.smoke) {
        const t = (age - p.delay) / SMOKE_MS;
        p.mesh.visible = t > 0 && t < 1;
        if (!p.mesh.visible) { if (t <= 0) alive = true; continue; }
        alive = true;
        p.mesh.position.addScaledVector(p.drift, dt);
        p.mesh.scale.setScalar(p.size * (0.6 + t * 1.6));
        (p.mesh.material as THREE.MeshLambertMaterial).opacity = 0.75 * (1 - t);
      }
      if (!alive) {
        this.removeBurst(b);
        this.bursts.splice(i, 1);
      }
    }
  }
}
